import { Autocomplete, Grid, InputAdornment } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { getBackendErrors, getErrorMessage } from "api/errors";

import { useMember } from "api/members";
import { usePoints } from "api/points";
import { useWallets } from "api/wallets";
import { FormButtons } from "components/FormControls/FormButtons";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { InfoBox } from "components/Layout/InfoBox";
import { Modals } from "components/Layout/Member/MemberList/MemberList";
import { MemberListItem } from "components/Tooltips/ClaimRewardModal";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import { BackendMemberModel } from "types/members";
import {
  PointsTransferTypes,
  PointsTransferTypesUnion,
} from "types/unit-transfers";
import { backendErrors } from "utilities/backendErrors";

interface Props {
  closeModal: () => void;
  initialMember?: string;
  initialMode: PointsTransferTypesUnion;
  isModalOpen: boolean;
  setModal?: (modal: { member: string; mode: number }) => void;
}

type FormValues = {
  comment: string;
  customer: string;
  expiresInDays?: number;
  lockedUntilDays?: number;
  overrideUnitsSettingsRules: boolean;
  points: number;
  pointsTransferType: PointsTransferTypesUnion;
};

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    modalButtons: {
      justifyContent: "center",
      margin: theme.spacing(24, 0, 0),
      "& button": {
        flex: 1,
      },
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      "& + &": { marginTop: 0 },
    },
    fullWidth: {
      width: "100%",
      "& > div": {
        width: "100%",
      },
    },
    link: { cursor: "pointer", textDecoration: "underline" },
    boldText: { fontWeight: "bold" },
  }),
);

export const AddUnitsModal = ({
  closeModal,
  initialMember,
  initialMode,
  isModalOpen,
  setModal,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { addPoints, spendPoints } = usePoints();
  const [status, setStatus] = useState<{ ""?: [] }>();

  const { useInfiniteWalletsList } = useWallets();
  const [walletTypeSearch, setWalletTypeSearch] = useState<string>();

  const {
    data: infiniteWalletsList,
    size: walletsSize,
    setSize: setWalletsSize,
    isValidating,
  } = useInfiniteWalletsList({
    filters: walletTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: walletTypeSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: walletsList, anyElementsToFetch: anyWalletsToFetch } =
    useManageInfiniteScroll(infiniteWalletsList || []);

  const getMemberLabel = (option: BackendMemberModel) => {
    if (option.email) {
      return option.email;
    } else if (option.loyaltyCardNumber) {
      return `${message({ id: "loyaltyCardNumber" })}: ${
        option.loyaltyCardNumber
      }`;
    } else if (option.phone) {
      return `${message({ id: "phoneNumber" })}: ${option.phone}`;
    } else if (option.customerId) {
      return `${message({ id: "customerId" })}: ${option.customerId}`;
    } else {
      return "";
    }
  };
  const { useMembersList } = useMember();
  const [searchedMemberValue, setSearchedMemberValue] = useState("");
  const { data: searchedMembers } = useMembersList({
    filters: searchedMemberValue
      ? [
          {
            condition: "",
            query: "identifier",
            value: searchedMemberValue,
          },
        ]
      : [],
  });

  const isDeductPointsAction = initialMode === PointsTransferTypes.SPEND_POINTS;

  return (
    <OLModal
      header={message({
        id: isDeductPointsAction
          ? "deductUnitsFromTheMembersAccount"
          : "addUnitsToTheMembersAccount",
      })}
      onClose={() => closeModal()}
      open={isModalOpen}
      modalWidth={547}
    >
      <Formik
        enableReinitialize
        initialValues={{
          comment: "",
          customer: initialMember || "",
          overrideUnitsSettingsRules: false,
          points: 0,
          pointsTransferType: initialMode,
        }}
        onSubmit={async (values: FormValues, { setSubmitting, setErrors }) => {
          setSubmitting(true);
          setStatus({});

          if (isDeductPointsAction) {
            delete values?.expiresInDays;
            delete values?.lockedUntilDays;
          }

          try {
            const { pointsTransferType, overrideUnitsSettingsRules, ...body } =
              {
                ...values,
              };
            const response = isDeductPointsAction
              ? await spendPoints(body)
              : await addPoints(body);
            if (response?.data?.transferId) {
              closeModal();
              notify(message({ id: "unitsTransferAdded" }));
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            if (errors) {
              setErrors(errors);
            }
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <>
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}
              </>
              <Grid container columnSpacing={8} rowSpacing={10} mb={10}>
                <Grid md={12} item>
                  <InfoBox
                    type="warning"
                    text={message(
                      { id: "unitTransferInfo" },
                      {
                        transferType: (
                          <span className={classes.boldText}>
                            {message({
                              id: isDeductPointsAction
                                ? "deductUnits"
                                : "addUnits",
                            }).toLowerCase()}
                          </span>
                        ),
                        operator: message({
                          id: isDeductPointsAction ? "from" : "to",
                        }).toLowerCase(),
                        alternativeType: message({
                          id: isDeductPointsAction ? "addUnits" : "deductUnits",
                        }).toLowerCase(),
                        link: (
                          <span
                            className={classes.link}
                            onClick={() => {
                              setModal?.({
                                member: initialMember || "",
                                mode: isDeductPointsAction
                                  ? Modals.Add
                                  : Modals.Deduct,
                              });
                            }}
                          >
                            {message({ id: "clickHere" }).toLowerCase()}
                          </span>
                        ),
                      },
                    )}
                  />
                </Grid>
                {!initialMember && (
                  <Grid md={12} item>
                    <Autocomplete
                      className={classes.fullWidth}
                      renderOption={(props, member: BackendMemberModel) => (
                        <MemberListItem
                          props={{ ...props, "data-hj-suppress": true }}
                          member={member}
                        />
                      )}
                      popupIcon={<ChevronDownIcon />}
                      filterOptions={(options: BackendMemberModel[]) => options}
                      getOptionLabel={(option: BackendMemberModel) =>
                        getMemberLabel(option)
                      }
                      onChange={(_, newInputValue) => {
                        if (newInputValue) {
                          setFieldValue(
                            "customer",
                            newInputValue["customerId"],
                          );
                        } else {
                          setFieldValue("customer", "");
                        }
                      }}
                      onInputChange={(e: any) =>
                        setSearchedMemberValue(e.target["value"])
                      }
                      options={searchedMembers ? searchedMembers.items : []}
                      renderInput={(params) => (
                        <TextInput
                          {...params}
                          label={
                            <RequiredFieldLabel
                              text={message({ id: "member" })}
                            />
                          }
                          name="customer"
                          placeholder={message({ id: "selectMemberInfo" })}
                          data-test-id="unit-transfers-list-add-units-transfer-modal-member-input"
                        />
                      )}
                      data-hj-suppress
                    />
                  </Grid>
                )}
                <Grid md={12} item>
                  <SelectInput
                    displaySearch
                    data-test-id="unit-transfers-list-add-units-transfer-modal-wallet-type-input"
                    items={walletsList.map((wallet) => ({
                      label: wallet.name,
                      id: wallet.code,
                    }))}
                    asyncSearch={(searchedQuery) =>
                      setWalletTypeSearch(searchedQuery)
                    }
                    label={
                      <RequiredFieldLabel
                        text={message({ id: "walletType" })}
                      />
                    }
                    name="walletCode"
                    placeholder={message({ id: "walletType" })}
                    loadMoreElements={() =>
                      !isValidating && setWalletsSize(walletsSize + 1)
                    }
                    anyElementsToFetch={anyWalletsToFetch}
                  />
                </Grid>
                <Grid md={12} item>
                  <NumberFormatInput
                    allowNegative={false}
                    allowDecimals
                    label={
                      <RequiredFieldLabel
                        text={message({ id: "numberOfUnits" })}
                      />
                    }
                    name="points"
                    data-test-id="unit-transfers-list-add-units-transfer-modal-number-of-units-input"
                  />
                </Grid>
                <Grid md={12} item>
                  <TextInput
                    className={classes.fullWidth}
                    label={message({ id: "comment" })}
                    name="comment"
                    data-test-id="unit-transfers-list-comment-input"
                    width="100%"
                  />
                </Grid>
                <Grid md={12} item>
                  <TextInput
                    className={classes.fullWidth}
                    label={message({ id: "externalTransferId" })}
                    name="externalTransferId"
                    data-test-id="unit-transfers-list-external-transfer-id-input"
                    width="100%"
                  />
                </Grid>
                {!isDeductPointsAction && (
                  <>
                    <Grid item xs={12}>
                      <Toggle
                        data-test-id="override-units-settings-rules"
                        label={message({ id: "overrideUnitsSettingsRules" })}
                        name="overrideUnitsSettingsRules"
                        onChange={() => {
                          setFieldValue("lockedUntilDays", undefined);
                          setFieldValue("expiresInDays", undefined);
                        }}
                      />
                    </Grid>
                    {values?.overrideUnitsSettingsRules && (
                      <>
                        <Grid md={12} item>
                          {message({ id: "overrideUnitsSettingsRulesInfo" })}
                        </Grid>
                        <Grid md={12} item>
                          <NumberFormatInput
                            allowNegative={false}
                            className={classes.fullWidth}
                            data-test-id="unit-transfers-list-add-units-transfer-modal-units-pending-period-input"
                            label={message({ id: "unitsPendingPeriod" })}
                            name="lockedUntilDays"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {message({ id: "days" })}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid md={12} item>
                          <NumberFormatInput
                            allowNegative={false}
                            className={classes.fullWidth}
                            data-test-id="unit-transfers-list-add-units-transfer-modal-units-expiration-period-input"
                            label={message({ id: "unitsExpirationPeriod" })}
                            name="expiresInDays"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {message({ id: "days" })}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
              <FormButtons
                className={classes.modalButtons}
                disableSubmit={isSubmitting}
                onCancelClick={() => closeModal()}
                onSubmitClick={() => handleSubmit()}
                submitMessage={message({
                  id: isDeductPointsAction ? "deductUnits" : "addUnits",
                })}
              />
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
